import { useCallback, useContext, useEffect, useMemo, useSyncExternalStore } from 'react';
import { RewardsCoordinatorContext } from '../../../coordinators/rewards/RewardsCoordinatorContext';
import { RewardIcon } from './RewardIcon';

interface PointsActivityProps {
  onBack: () => void;
  backLabel?: string;
}

type EntryType = 'earned' | 'redeemed' | 'expired' | 'adjusted';

function typeLabel(type: EntryType): string {
  switch (type) {
    case 'earned': return 'Earned';
    case 'redeemed': return 'Redeemed';
    case 'expired': return 'Expired';
    case 'adjusted': return 'Adjusted';
  }
}

function formatAmount(amount: number): string {
  if (amount > 0) return `+${amount.toLocaleString()}`;
  return amount.toLocaleString();
}

function deltaColorStyle(amount: number): string {
  return amount < 0 ? 'rgba(255,255,255,0.6)' : '#FBBF24';
}

function formatDate(isoDate: string): string {
  const d = new Date(isoDate);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export const PointsActivity = ({ onBack, backLabel = 'Account' }: PointsActivityProps) => {
  const coordinator = useContext(RewardsCoordinatorContext);
  const activityManager = useMemo(() => coordinator?.getActivityManager(), [coordinator]);

  const defaultState = useMemo(() => ({ entries: [] as never[], loading: true, loadingMore: false, hasMore: true, error: null }), []);

  const state = useSyncExternalStore(
    useCallback((cb) => activityManager?.subscribe(cb) ?? (() => {}), [activityManager]),
    () => activityManager?.getState() ?? defaultState,
  );

  useEffect(() => {
    activityManager?.fetchInitial();
  }, [activityManager]);

  const { entries = [], loading, loadingMore, hasMore, error } = state;
  return (
    <div className='flex min-h-0 flex-1 flex-col'>
      {/* Back row */}
      <button type='button' onClick={onBack}
        className='mb-3 shrink-0 self-start border-none bg-transparent p-0 text-sm font-semibold text-[#FFFFFF]/90 transition-opacity hover:opacity-80'>
        &larr; {backLabel}
      </button>
      <p className='shrink-0 text-[18px] font-semibold text-white' style={{ marginBottom: 4 }}>Tokens activity</p>
      <p className='shrink-0 text-[10px] text-[#FFFFFF]/70' style={{ marginTop: 6 }}>Tokens expire one year after earning</p>
      <div className='h-[10px] shrink-0' />

      {/* Scrollable content */}
      <div className='hr-scroll min-h-0 flex-1 overflow-y-auto' style={{ paddingRight: 4 }}>
        {loading ? (
          <p className='animate-pulse py-6 text-center text-sm text-[#FFFFFF]/80'>Loading activity...</p>
        ) : error && entries.length === 0 ? (
          <p className='py-6 text-center text-sm text-[#FCA5A5]'>{error}</p>
        ) : entries.length === 0 ? (
          <p className='py-6 text-center text-sm text-[#FFFFFF]/70'>No activity yet</p>
        ) : (
          <>
            <div className='flex flex-col gap-1.5 pb-2'>
              {entries.map((entry) => (
                <div key={entry.id}
                  style={{
                    padding: '8px 10px',
                    borderRadius: 8,
                    border: '1px solid rgba(255,255,255,0.15)',
                    background: 'rgba(255,255,255,0.08)',
                  }}
                >
                  <div className='flex items-center justify-between'>
                    <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                      <span className='text-sm font-semibold text-white'>{typeLabel(entry.type)}</span>
                      <span className='text-[10px] text-[#FFFFFF]/70'>
                        {entry.description}
                        {entry.machineName && <> &middot; {entry.machineName}</>}
                        {' '}&middot; {formatDate(entry.createdAt)}
                      </span>
                    </div>
                    <span className='inline-flex shrink-0 items-center gap-1 text-sm font-semibold'
                      style={{ color: deltaColorStyle(entry.amount) }}>
                      <RewardIcon size={14} />
                      {formatAmount(entry.amount)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {loadingMore && (
              <p className='animate-pulse py-4 text-center text-xs text-[#FFFFFF]/70'>Loading more...</p>
            )}

            {hasMore && !loadingMore && (
              <button
                type='button'
                onClick={() => activityManager?.loadMore()}
                className='mt-3 w-full rounded-lg bg-[#FFFFFF]/5 py-2 text-xs font-medium text-[#FFFFFF]/80 transition-colors hover:bg-[#FFFFFF]/10'
              >
                Load earlier
              </button>
            )}

            {error && entries.length > 0 && (
              <p className='py-2 text-center text-xs text-[#FCA5A5]'>{error}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};
